import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Coins, Search, Snowflake, Plus, Minus, Zap, DollarSign, Landmark, Copy, Vault, ArrowRight, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Switch } from "@/components/ui/switch";

const input = "bg-pb-elevated border border-pb-border px-3 py-2 text-white focus:outline-none focus:border-pb-red text-sm w-full";
const TYPE_COLORS = { purchase: "#A3E635", cash_credit: "#A3E635", spend: "#F59E0B", pos_charge: "#F59E0B", transfer_in: "#22D3EE", transfer_out: "#22D3EE", admin_deduct: "#EF4444", bonus_credit: "#C084FC", refund: "#4ADE80", payment_in: "#A3E635", mint: "#F59E0B", mint_circulate: "#F59E0B", bonus_mint: "#EF4444", bonus_circulate: "#EF4444", bonus_payout: "#EF4444", bonus_received: "#EF4444", treasury_out: "#F87171" };

function VaultBox({ vault, onChanged }) {
  const [mintAmt, setMintAmt] = useState("");
  const [circAmt, setCircAmt] = useState("");
  const [busy, setBusy] = useState(false);
  const v = vault || { supply: 0, minted_total: 0, circulated_total: 0 };

  const run = async (endpoint, amt, ok) => {
    const n = Number(amt);
    if (!n || n <= 0) return toast.error("Enter a coin amount first");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bullion/vault/${endpoint}`, { amount: n });
      toast.success(ok(data));
      setMintAmt(""); setCircAmt("");
      await onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-pb-surface border border-amber-500/50 p-5" data-testid="bullion-vault">
      <div className="flex items-center gap-2 mb-1">
        <Vault className="w-5 h-5 text-amber-400" />
        <span className="font-display font-black text-lg uppercase">The Vault</span>
      </div>
      <p className="text-xs text-zinc-500 mb-4">Mint new BULLION into the supply, then circulate it straight into the treasury wallet (BLC-BULLPEN). Every mint and circulation is recorded in the ledger.</p>
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Mint Coin Amount</label>
          <div className="flex gap-2">
            <input data-testid="vault-mint-input" type="number" min="0.01" step="0.01" value={mintAmt} placeholder="0"
              onChange={(e) => setMintAmt(e.target.value)}
              className="flex-1 bg-pb-elevated border border-pb-border px-3 py-2.5 text-white font-mono focus:outline-none focus:border-amber-400" />
            <button data-testid="vault-mint-btn" disabled={busy} onClick={() => run("mint", mintAmt, (d) => `Minted ${Number(mintAmt).toLocaleString()} BLC — supply is now ${d.vault.supply.toLocaleString()} BLC`)}
              className="shrink-0 flex items-center gap-1.5 bg-amber-400 text-black font-black uppercase tracking-widest px-6 hover:opacity-90 active:scale-95 transition-all disabled:opacity-50 text-xs">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Mint
            </button>
          </div>
          <div className="mt-4 border border-amber-500/30 bg-amber-500/5 p-4" data-testid="vault-supply-box">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Supply (minted, uncirculated)</div>
            <div className="font-display font-black text-3xl text-amber-400" data-testid="vault-supply">
              {v.supply.toLocaleString()}<span className="text-sm text-zinc-500 ml-1">BLC</span>
            </div>
            <div className="text-[10px] text-zinc-600 mt-1">Minted all-time: {v.minted_total.toLocaleString()} · Circulated all-time: {v.circulated_total.toLocaleString()}</div>
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Circulate to Treasury (BLC-BULLPEN)</label>
          <div className="flex gap-2">
            <input data-testid="vault-circulate-input" type="number" min="0.01" step="0.01" max={v.supply} value={circAmt} placeholder="0"
              onChange={(e) => setCircAmt(e.target.value)}
              className="flex-1 bg-pb-elevated border border-pb-border px-3 py-2.5 text-white font-mono focus:outline-none focus:border-pb-red" />
            <button data-testid="vault-circulate-btn" disabled={busy || v.supply <= 0} onClick={() => run("circulate", circAmt, (d) => `Circulated ${Number(circAmt).toLocaleString()} BLC to the treasury — balance ${d.treasury_balance.toLocaleString()} BLC`)}
              className="shrink-0 flex items-center gap-1.5 bg-pb-red text-black font-black uppercase tracking-widest px-4 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50 text-xs">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />} Circulate
            </button>
          </div>
          <p className="text-[10px] text-zinc-600 mt-2">Moves coins from the Supply into the treasury wallet — they land in your Bullion Treasury balance above and become spendable in the ecosystem.</p>
        </div>
      </div>
    </div>
  );
}

function RedVaultBox({ redVault, onChanged }) {
  const [mintAmt, setMintAmt] = useState("");
  const [circAmt, setCircAmt] = useState("");
  const [busy, setBusy] = useState(false);
  const v = redVault || { supply: 0, minted_total: 0, circulated_total: 0, balance: 0, code: "BLC-REDVAULT" };

  const run = async (endpoint, amt, ok) => {
    const n = Number(amt);
    if (!n || n <= 0) return toast.error("Enter a coin amount first");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bullion/red-vault/${endpoint}`, { amount: n });
      toast.success(ok(data));
      setMintAmt(""); setCircAmt("");
      await onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-red-950/30 border border-red-600/60 p-5" data-testid="red-vault-storage">
      <div className="flex flex-col sm:flex-row items-center gap-6 mb-5">
        <div className="bg-white p-2.5 shrink-0">
          <QRCodeSVG value={v.code} size={96} data-testid="red-vault-qr" />
        </div>
        <div className="flex-1 text-center sm:text-left">
          <div className="flex items-center gap-2 justify-center sm:justify-start mb-1">
            <Vault className="w-5 h-5 text-red-500" />
            <span className="font-display font-black text-lg uppercase text-red-400">Red Vault Storage</span>
          </div>
          <p className="text-xs text-zinc-500 mb-2">The bonus token vault — mint bonus BLC into supply, circulate it into this wallet, and every bonus token given on BLC purchases is paid out from here. It can receive and send bonus tokens via its wallet address.</p>
          <button data-testid="red-vault-code"
            onClick={() => { navigator.clipboard.writeText(v.code); toast.success("Red Vault address copied"); }}
            className="inline-flex items-center gap-2 font-mono text-red-400 text-sm border border-red-600/40 px-3 py-1.5 hover:border-red-400 transition-colors">
            {v.code} <Copy className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="text-center sm:text-right shrink-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Bonus Token Balance</div>
          <div className="font-display font-black text-4xl text-red-500" data-testid="red-vault-balance">
            {v.balance.toFixed(2)}<span className="text-sm text-zinc-500 ml-1">BLC</span>
          </div>
          <div className="text-[10px] text-zinc-600 mt-1">Ready to pay out as purchase bonuses</div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Mint Bonus Token Amount</label>
          <div className="flex gap-2">
            <input data-testid="red-vault-mint-input" type="number" min="0.01" step="0.01" value={mintAmt} placeholder="0"
              onChange={(e) => setMintAmt(e.target.value)}
              className="flex-1 bg-pb-elevated border border-pb-border px-3 py-2.5 text-white font-mono focus:outline-none focus:border-red-500" />
            <button data-testid="red-vault-mint-btn" disabled={busy} onClick={() => run("mint", mintAmt, (d) => `Minted ${Number(mintAmt).toLocaleString()} bonus BLC — supply is now ${d.red_vault.supply.toLocaleString()} BLC`)}
              className="shrink-0 flex items-center gap-1.5 bg-red-600 text-white font-black uppercase tracking-widest px-6 hover:bg-red-500 active:scale-95 transition-all disabled:opacity-50 text-xs">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Mint
            </button>
          </div>
          <div className="mt-4 border border-red-600/40 bg-red-600/5 p-4" data-testid="red-vault-supply-box">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Bonus Supply (minted, uncirculated)</div>
            <div className="font-display font-black text-3xl text-red-500" data-testid="red-vault-supply">
              {v.supply.toLocaleString()}<span className="text-sm text-zinc-500 ml-1">BLC</span>
            </div>
            <div className="text-[10px] text-zinc-600 mt-1">Minted all-time: {v.minted_total.toLocaleString()} · Circulated all-time: {v.circulated_total.toLocaleString()}</div>
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Circulate to Red Vault Wallet ({v.code})</label>
          <div className="flex gap-2">
            <input data-testid="red-vault-circulate-input" type="number" min="0.01" step="0.01" max={v.supply} value={circAmt} placeholder="0"
              onChange={(e) => setCircAmt(e.target.value)}
              className="flex-1 bg-pb-elevated border border-pb-border px-3 py-2.5 text-white font-mono focus:outline-none focus:border-red-500" />
            <button data-testid="red-vault-circulate-btn" disabled={busy || v.supply <= 0} onClick={() => run("circulate", circAmt, (d) => `Circulated ${Number(circAmt).toLocaleString()} bonus BLC — wallet balance ${d.red_vault.balance.toLocaleString()} BLC`)}
              className="shrink-0 flex items-center gap-1.5 bg-red-600 text-white font-black uppercase tracking-widest px-4 hover:bg-red-500 active:scale-95 transition-all disabled:opacity-50 text-xs">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />} Circulate
            </button>
          </div>
          <p className="text-[10px] text-zinc-600 mt-2">Moves bonus tokens from the Bonus Supply into the Red Vault wallet balance — bonus tokens granted on BLC purchases are automatically deducted from this balance.</p>
        </div>
      </div>
    </div>
  );
}

export default function BullionControl() {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [member, setMember] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [bucket, setBucket] = useState("regular");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/bullion");
      setData(data);
      setSettings((s) => s || data.settings);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/admin/bullion/find", { params: { q } });
        setResults(data);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/admin/bullion/settings", {
        ...settings,
        rate_usd: Number(settings.rate_usd), fee_pct: Number(settings.fee_pct), fee_flat: Number(settings.fee_flat),
        packs: settings.packs.map((p) => ({ coins: Number(p.coins), bonus: Number(p.bonus) || 0 })),
      });
      toast.success("BULLION settings saved");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const pickMember = async (m) => { setMember(m); setResults([]); setQ(""); };
  const refreshMember = async (id) => {
    const { data } = await api.get("/admin/bullion/find", { params: { q: id } });
    // find by code/email won't match id; re-search by member email
  };

  const act = async (endpoint, successMsg) => {
    const coins = Number(amount);
    if (!member || !coins || coins <= 0) return toast.error("Pick a member and enter a coin amount");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bullion/${endpoint}`, { user_id: member.id, coins, note: note || null, bucket });
      toast.success(successMsg(data));
      setAmount(""); setNote("");
      setMember({ ...member,
        balance: data.bucket === "bonus" ? member.balance : data.balance,
        bonus_balance: data.bucket === "bonus" ? data.balance : member.bonus_balance });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleFreeze = async () => {
    try {
      await api.post("/admin/bullion/freeze", { user_id: member.id, frozen: !member.frozen });
      toast.success(member.frozen ? "Wallet unfrozen" : "Wallet frozen");
      setMember({ ...member, frozen: !member.frozen });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data || !settings) return <div className="p-4 text-sm text-zinc-500 animate-pulse">Loading BULLION system…</div>;

  const s = data.stats;
  const cashPreview = Number(amount) > 0 && settings.apply_fee_on_purchase
    ? (Number(amount) * Number(settings.rate_usd) * (1 + Number(settings.fee_pct) / 100) + Number(settings.fee_flat)).toFixed(2)
    : (Number(amount) * Number(settings.rate_usd)).toFixed(2);
  const chargePreview = Number(amount) > 0 && settings.apply_fee_on_spend
    ? (Number(amount) + (Number(amount) * Number(settings.rate_usd) * Number(settings.fee_pct) / 100 + Number(settings.fee_flat)) / Number(settings.rate_usd)).toFixed(2)
    : Number(amount).toFixed(2);

  return (
    <div className="mt-4 space-y-6" data-testid="bullion-control">
      {/* Treasury */}
      {data.treasury && (
        <div className="bg-pb-surface border border-pb-red/50 p-5 flex flex-col sm:flex-row items-center gap-6" data-testid="bullion-treasury">
          <div className="bg-white p-2.5 shrink-0">
            <QRCodeSVG value={data.treasury.code} size={96} data-testid="treasury-qr" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <div className="flex items-center gap-2 justify-center sm:justify-start mb-1">
              <Landmark className="w-5 h-5 text-pb-red" />
              <span className="font-display font-black text-lg uppercase">Central Bank — Bullion Treasury</span>
            </div>
            <p className="text-xs text-zinc-500 mb-2">The ecosystem's money supply — not a member wallet. Coins members spend return here (leaving circulation); coins it pays out enter member circulation.</p>
            <button data-testid="treasury-code"
              onClick={() => { navigator.clipboard.writeText(data.treasury.code); toast.success("Treasury address copied"); }}
              className="inline-flex items-center gap-2 font-mono text-pb-cyan text-sm border border-pb-border px-3 py-1.5 hover:border-pb-cyan transition-colors">
              {data.treasury.code} <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="text-center sm:text-right shrink-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Treasury Balance</div>
            <div className="font-display font-black text-4xl text-pb-red" data-testid="treasury-balance">
              {data.treasury.balance.toFixed(2)}<span className="text-sm text-zinc-500 ml-1">BLC</span>
            </div>
            {data.treasury.bonus_balance > 0 && (
              <div className="text-xs text-purple-300 font-mono" data-testid="treasury-bonus">+ {data.treasury.bonus_balance.toFixed(2)} bonus</div>
            )}
          </div>
        </div>
      )}
      {/* The Vault */}
      <VaultBox vault={data.vault} onChanged={load} />

      {/* Red Vault Storage (bonus tokens) */}
      <RedVaultBox redVault={data.red_vault} onChanged={load} />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="Member Circulation" value={`${s.circulation.toLocaleString()} BLC`} color="#A3E635" testid="blc-stat-circulation" />
        <Stat label="Active Wallets" value={s.wallets} color="#22D3EE" testid="blc-stat-wallets" />
        <Stat label="Cash Collected" value={`$${s.cash_collected.toLocaleString()}`} color="#A3E635" testid="blc-stat-cash" />
        <Stat label="Service Fees ($)" value={`$${s.fees_usd.toLocaleString()}`} color="#F59E0B" testid="blc-stat-fees" />
        <Stat label="Coins Spent" value={`${s.coins_spent.toLocaleString()} BLC`} color="#F59E0B" testid="blc-stat-spent" />
      </div>

      {/* POS / member manager */}
      <div className="bg-pb-surface border border-pb-border p-5">
        <div className="flex items-center gap-2 mb-1">
          <Zap className="w-4 h-4 text-pb-red" />
          <span className="font-display font-bold text-lg uppercase">Event Point of Sale</span>
        </div>
        <p className="text-xs text-zinc-500 mb-4">Find a member by name, email or wallet code (from their QR). Load coins for cash, charge purchases, or freeze the wallet.</p>
        <div className="relative max-w-md">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="blc-member-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search member or scan code (BLC-XXXXXX)…"
            className={`${input} pl-9`} />
          {results.length > 0 && (
            <div className="absolute z-20 top-full left-0 right-0 bg-pb-elevated border border-pb-border max-h-56 overflow-y-auto">
              {results.map((m) => (
                <button key={m.id} data-testid={`blc-member-result-${m.id}`} onClick={() => pickMember(m)}
                  className="w-full text-left px-3 py-2.5 hover:bg-pb-surface border-b border-pb-border/40 text-sm">
                  <span className="font-semibold">{m.name}</span>
                  <span className="text-zinc-500 text-xs ml-2">{m.email}</span>
                  <span className="font-mono text-pb-red text-xs float-right">{m.balance.toFixed(2)} BLC</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {member && (
          <div className="mt-4 border border-pb-cyan/40 bg-pb-cyan/5 p-4" data-testid="blc-member-card">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <div className="font-display font-bold text-lg">{member.name} {member.frozen && <Snowflake className="w-4 h-4 text-red-400 inline ml-1" />}</div>
                <div className="text-xs text-zinc-500">{member.email} · <span className="font-mono text-pb-cyan">{member.code || "no code yet"}</span></div>
              </div>
              <div className="text-right">
                <div className="font-display font-black text-2xl text-pb-red" data-testid="blc-member-balance">{member.balance.toFixed(2)} BLC</div>
                <div className="text-xs text-purple-300 font-mono" data-testid="blc-member-bonus">+ {Number(member.bonus_balance || 0).toFixed(2)} bonus</div>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-3" data-testid="blc-bucket-toggle">
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Balance:</span>
              {[["regular", "BULLION"], ["bonus", "Bonus Coins"]].map(([v, l]) => (
                <button key={v} data-testid={`blc-bucket-${v}`} onClick={() => setBucket(v)}
                  className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                    bucket === v ? "border-purple-400 text-purple-300 bg-purple-400/10" : "border-pb-border text-zinc-500 hover:text-white"}`}>
                  {l}
                </button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <input data-testid="blc-pos-amount" type="number" min={0.01} value={amount} onChange={(e) => setAmount(e.target.value)}
                placeholder="Coins" className={input} />
              <input data-testid="blc-pos-note" value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Note (e.g. Cash at Spring Slam, Court fee…)" className={input} />
            </div>
            {Number(amount) > 0 && (
              <div className="text-xs text-zinc-500 font-mono mb-3" data-testid="blc-pos-preview">
                Credit: collect <span className="text-pb-red">${cashPreview}</span> cash · Charge: deducts <span className="text-amber-400">{chargePreview} BLC</span> incl. service fee
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <button data-testid="blc-pos-credit" onClick={() => act("credit", (d) => `Loaded ${d.credited} BLC — collect $${d.cash_to_collect} cash`)} disabled={busy}
                className="flex items-center gap-1.5 bg-pb-red text-black font-bold uppercase tracking-widest text-xs px-4 py-2.5 hover:bg-[#BEF264] transition-all disabled:opacity-40">
                <Plus className="w-3.5 h-3.5" /> Load for Cash
              </button>
              <button data-testid="blc-pos-charge" onClick={() => act("charge", (d) => `Charged ${d.charged} BLC (${d.service_fee_coins} fee)`)} disabled={busy}
                className="flex items-center gap-1.5 border border-amber-500/60 text-amber-400 hover:bg-amber-500/10 font-bold uppercase tracking-widest text-xs px-4 py-2.5 transition-all disabled:opacity-40">
                <DollarSign className="w-3.5 h-3.5" /> Charge Purchase
              </button>
              <button data-testid="blc-pos-deduct" onClick={() => act("deduct", (d) => `Deducted ${d.deducted} BLC`)} disabled={busy}
                className="flex items-center gap-1.5 border border-pb-border hover:border-red-500 hover:text-red-400 font-bold uppercase tracking-widest text-xs px-4 py-2.5 transition-colors disabled:opacity-40">
                <Minus className="w-3.5 h-3.5" /> Deduct
              </button>
              <button data-testid="blc-pos-freeze" onClick={toggleFreeze}
                className="flex items-center gap-1.5 border border-pb-border hover:border-pb-cyan hover:text-pb-cyan font-bold uppercase tracking-widest text-xs px-4 py-2.5 transition-colors">
                <Snowflake className="w-3.5 h-3.5" /> {member.frozen ? "Unfreeze" : "Freeze"} Wallet
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Settings */}
      <div className="bg-pb-surface border border-pb-border p-5">
        <div className="flex items-center gap-2 mb-4">
          <Coins className="w-4 h-4 text-pb-red" />
          <span className="font-display font-bold text-lg uppercase">Coin Settings</span>
        </div>
        <div className="grid sm:grid-cols-3 gap-4 mb-4">
          <Field label="Coin Rate (1 BLC = $)" testid="blc-setting-rate">
            <input type="number" min={0.01} step={0.01} value={settings.rate_usd} data-testid="blc-setting-rate-input"
              onChange={(e) => setSettings({ ...settings, rate_usd: e.target.value })} className={input} />
          </Field>
          <Field label="Service Fee (%)" testid="blc-setting-feepct">
            <input type="number" min={0} max={100} step={0.1} value={settings.fee_pct} data-testid="blc-setting-feepct-input"
              onChange={(e) => setSettings({ ...settings, fee_pct: e.target.value })} className={input} />
          </Field>
          <Field label="Service Fee Flat ($)" testid="blc-setting-feeflat">
            <input type="number" min={0} step={0.5} value={settings.fee_flat} data-testid="blc-setting-feeflat-input"
              onChange={(e) => setSettings({ ...settings, fee_flat: e.target.value })} className={input} />
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <Toggle label="System Enabled" checked={settings.enabled} testid="blc-toggle-enabled"
            onChange={(v) => setSettings({ ...settings, enabled: v })} />
          <Toggle label="Member Transfers" checked={settings.transfers_enabled} testid="blc-toggle-transfers"
            onChange={(v) => setSettings({ ...settings, transfers_enabled: v })} />
          <Toggle label="Fee on Coin Purchase" checked={settings.apply_fee_on_purchase} testid="blc-toggle-fee-purchase"
            onChange={(v) => setSettings({ ...settings, apply_fee_on_purchase: v })} />
          <Toggle label="Fee on Spending" checked={settings.apply_fee_on_spend} testid="blc-toggle-fee-spend"
            onChange={(v) => setSettings({ ...settings, apply_fee_on_spend: v })} />
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Where coins are accepted</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
          {[["store", "Store & Passes"], ["sessions", "Session Bookings"], ["league", "League Entry"], ["membership", "Memberships"], ["tournaments", "Tournaments"]].map(([k, l]) => (
            <Toggle key={k} label={l} checked={settings.accept?.[k] !== false} testid={`blc-accept-${k}`}
              onChange={(v) => setSettings({ ...settings, accept: { ...settings.accept, [k]: v } })} />
          ))}
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-purple-300 mb-2">Where bonus coins are accepted</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
          {[["store", "Store & Passes"], ["sessions", "Session Bookings"], ["league", "League Entry"], ["membership", "Memberships"], ["tournaments", "Tournaments"]].map(([k, l]) => (
            <Toggle key={k} label={l} checked={settings.accept_bonus?.[k] !== false} testid={`blc-accept-bonus-${k}`}
              onChange={(v) => setSettings({ ...settings, accept_bonus: { ...settings.accept_bonus, [k]: v } })} />
          ))}
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Bonus Packs (buy X get Y free)</div>
        <div className="space-y-2 mb-4">
          {settings.packs.map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="number" min={1} value={p.coins} data-testid={`blc-pack-coins-${i}`}
                onChange={(e) => setSettings({ ...settings, packs: settings.packs.map((x, j) => j === i ? { ...x, coins: e.target.value } : x) })}
                className={`${input} w-28`} />
              <span className="text-xs text-zinc-500">BLC gets</span>
              <input type="number" min={0} value={p.bonus} data-testid={`blc-pack-bonus-${i}`}
                onChange={(e) => setSettings({ ...settings, packs: settings.packs.map((x, j) => j === i ? { ...x, bonus: e.target.value } : x) })}
                className={`${input} w-24`} />
              <span className="text-xs text-zinc-500">bonus</span>
              <button data-testid={`blc-pack-remove-${i}`} onClick={() => setSettings({ ...settings, packs: settings.packs.filter((_, j) => j !== i) })}
                className="text-zinc-600 hover:text-red-400 text-xs uppercase font-bold ml-2">Remove</button>
            </div>
          ))}
          <button data-testid="blc-pack-add" onClick={() => setSettings({ ...settings, packs: [...settings.packs, { coins: 100, bonus: 0 }] })}
            className="text-xs uppercase font-bold tracking-widest text-pb-cyan hover:text-white transition-colors">+ Add Pack</button>
        </div>
        <button data-testid="blc-settings-save" onClick={saveSettings} disabled={saving}
          className="bg-pb-red text-black font-bold uppercase tracking-widest px-5 py-2.5 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
          {saving ? "Saving…" : "Save Coin Settings"}
        </button>
      </div>

      {/* Ledger */}
      <div className="bg-pb-surface border border-pb-border">
        <div className="p-5 border-b border-pb-border font-display font-bold text-lg uppercase">Full Ledger (last 100)</div>
        {data.ledger.length === 0 ? (
          <div className="p-5 text-sm text-zinc-600">No coin activity yet.</div>
        ) : (
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-pb-surface">
                <tr className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-pb-border/60">
                  <th className="text-left px-4 py-3">Member</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-right px-4 py-3">Coins</th>
                  <th className="text-right px-4 py-3">Fee</th>
                  <th className="text-right px-4 py-3">USD</th>
                  <th className="text-left px-4 py-3">Note</th>
                  <th className="text-left px-4 py-3">By</th>
                  <th className="text-left px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.ledger.map((t) => (
                  <tr key={t.id} className="border-b border-pb-border/40 hover:bg-pb-elevated/50" data-testid="blc-ledger-row">
                    <td className="px-4 py-2.5 font-semibold whitespace-nowrap">{t.user_name}</td>
                    <td className="px-4 py-2.5"><span className="text-xs font-bold uppercase" style={{ color: TYPE_COLORS[t.type] || "#A1A1AA" }}>{t.type.replace("_", " ")}</span></td>
                    <td className={`px-4 py-2.5 text-right font-mono ${t.coins >= 0 ? "text-pb-red" : "text-zinc-300"}`}>{t.coins >= 0 ? "+" : ""}{Number(t.coins).toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-zinc-500">{t.fee_coins ? Number(t.fee_coins).toFixed(2) : t.fee_usd ? `$${Number(t.fee_usd).toFixed(2)}` : "—"}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-zinc-400">{t.usd != null ? `$${Number(t.usd).toFixed(2)}` : "—"}</td>
                    <td className="px-4 py-2.5 text-zinc-400 text-xs max-w-[220px] truncate">{t.counterparty_name ? `${t.type === "transfer_in" ? "from" : "to"} ${t.counterparty_name}` : t.note}</td>
                    <td className="px-4 py-2.5 text-zinc-500 text-xs">{t.by || "—"}</td>
                    <td className="px-4 py-2.5 text-zinc-600 text-xs whitespace-nowrap">{t.created_at ? new Date(t.created_at).toLocaleString() : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color, testid }) {
  return (
    <div className="bg-pb-elevated border border-pb-border p-3" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{label}</div>
      <div className="font-display font-black text-lg" style={{ color }}>{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{label}</label>
      {children}
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between bg-pb-elevated border border-pb-border px-3 py-2.5">
      <span className="text-xs font-semibold">{label}</span>
      <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} className="data-[state=checked]:bg-pb-red" />
    </div>
  );
}
